import React, { useState, useCallback } from "react";
import { Box, Grid, Typography, Button, Link, Stack } from "@mui/material";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { RecommendedCharger, OfficeWithPersona, VoiceItem } from "@/models/goFurther";
import { GetGoChargeApplicationFormUrl } from "@/common/CommonConstants";
import { GoFurtherAnalytics } from "@/util/analytics";
import { FONT_SIZE, FONT_WEIGHT, TEXT_COLOR, BACKGROUND_COLOR } from "../GoFurtherStyles";
import { SectionGridItemCard, SectionGridItemBannerCard, SecondaryPanel, GoFurtherExternalLink } from "./GoFurtherPrimitives";
import { getDriverGuideUrlAPI } from "@/util/api/driverGuides";
import toast from "@/components/hoc/SnackbarAlert";
// AB/CD いずれのレイアウトも MotivationSection を ActionSection 内に配置するため、
// ActionSection が GoFurtherMotivationSection を内包している。
import GoFurtherMotivationSection from "./GoFurtherMotivationSection";

// ── 型定義 ────────────────────────────────────────────────────────────────────

interface GoFurtherActionSectionProps {
  recommendedCharger: RecommendedCharger | null | undefined;
  streetViewEmbedUrl: string;
  googleMapUrl: string;
  goChargeUrl: string;
  selectedOffice: OfficeWithPersona | null;
  /** "B1" | "B2" | ... | "C" | "D" など。B 系統かどうかの判定に使用。 */
  personaType?: string;
  driverGuideId?: number | null;
  voiceItems?: VoiceItem[];
}

// ── ファイルプライベート定数 ───────────────────────────────────────────────────

const PERSONA_B_SECTION_SPACING = 4;

const PERSONA_B_PANEL_SX = {
  p: PERSONA_B_SECTION_SPACING,
  display: "flex",
  flexDirection: "column",
  gap: PERSONA_B_SECTION_SPACING,
} as const;

const PERSONA_B_WHITE_BLOCK_SX = {
  bgcolor: BACKGROUND_COLOR.WHITE,
  borderRadius: 2,
  p: PERSONA_B_SECTION_SPACING,
  display: "flex",
  flexDirection: "column",
  gap: PERSONA_B_SECTION_SPACING,
} as const;

const SPEECH_BUBBLE_BASE_SX = {
  position: "relative",
  alignSelf: "center",
  bgcolor: BACKGROUND_COLOR.WHITE,
  borderRadius: 1.5,
  py: 1.5,
  px: 2,
  textAlign: "center",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  fontSize: FONT_SIZE.caption,
  color: TEXT_COLOR.SECONDARY,
} as const;

const SPEECH_BUBBLE_SUBTEXT_SX = {
  mt: 0.25,
  fontSize: FONT_SIZE.caption,
  color: TEXT_COLOR.SECONDARY,
  fontWeight: FONT_WEIGHT.BOLD,
} as const;

const SECTION_CHIP_SX = {
  display: "inline-block",
  position: "relative",
  bgcolor: BACKGROUND_COLOR.GRAY,
  color: TEXT_COLOR.CONTRAST,
  borderRadius: 1.5,
  px: 2.5,
  py: 1.5,
  fontSize: FONT_SIZE.subtitle1,
  fontWeight: FONT_WEIGHT.BOLD,
  mb: 1,
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -8,
    left: "50%",
    transform: "translateX(-50%)",
    width: 0,
    height: 0,
    borderLeft: "8px solid transparent",
    borderRight: "8px solid transparent",
    borderTop: `8px solid ${BACKGROUND_COLOR.GRAY}`,
  },
} as const;

const EV_BENEFITS = [
  {
    title: "疲れにくい",
    text: "エンジン特有の振動や騒音がなく、座席のクッション性が良いので長時間運転しても疲れにくい",
  },
  {
    title: "楽に運転できる",
    text: "EVでは、アクセルの踏み加減で加速や減速を簡単に調整できるためLPGに比べて楽に運転できる",
  },
  {
    title: "充電中に休憩",
    text: "充電中に休憩や食事を取ることで時間を有効活用できる。充電中は路駐もしなくて良いので安心",
  },
] as const;

// ── 共通パネル ────────────────────────────────────────────────────────────────

// おすすめ充電器 + GO Charge マップ（CD ステップ2・Persona B セクション1 共通）
const RecommendedChargerPanel: React.FC<{
  recommendedCharger: RecommendedCharger | null | undefined;
  streetViewEmbedUrl: string;
  googleMapUrl: string;
  goChargeUrl: string;
}> = ({ recommendedCharger, streetViewEmbedUrl, googleMapUrl, goChargeUrl }) => {
  if (!recommendedCharger) return null;
  return (
    <Grid container spacing={2} alignItems="stretch" wrap="nowrap">
      <Grid item xs={7} sx={{ display: "flex" }}>
        <Box sx={{ bgcolor: BACKGROUND_COLOR.WHITE, borderRadius: 4, p: 2, flex: 1, minWidth: 0 }}>
          <Typography sx={{ display: "inline-block", bgcolor: BACKGROUND_COLOR.PRIMARY, color: TEXT_COLOR.CONTRAST, borderRadius: 1, px: 2, py: 0.5, fontSize: FONT_SIZE.caption, fontWeight: FONT_WEIGHT.BOLD }}>
            おすすめ充電器
          </Typography>
          <Typography sx={{ mt: 1, fontSize: FONT_SIZE.body1, fontWeight: FONT_WEIGHT.BOLD, color: TEXT_COLOR.PRIMARY }}>
            {recommendedCharger.spotName ?? "-"}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY }}>
            <WatchLaterIcon sx={{ fontSize: "1rem", verticalAlign: "middle", mr: 0.5 }} />
            {recommendedCharger.businessHourLabel ?? "-"}
          </Typography>
          {recommendedCharger.recommendationReason && (
            <Typography sx={{ mt: 0.5, fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY }}>
              <LocationOnIcon sx={{ fontSize: "1rem", verticalAlign: "middle", mr: 0.5 }} />
              {recommendedCharger.recommendationReason}
            </Typography>
          )}
          <Typography sx={{ mt: 0.5, fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY }}>
            <ElectricalServicesIcon sx={{ fontSize: "1rem", verticalAlign: "middle", mr: 0.5 }} />
            {recommendedCharger.connectorCount != null ? `${recommendedCharger.connectorCount}口` : "-"}
          </Typography>
          {streetViewEmbedUrl && (
            <Box
              component="iframe"
              src={streetViewEmbedUrl}
              title="おすすめ充電器のストリートビュー"
              loading="lazy"
              sx={{ width: "100%", height: 260, border: "0", borderRadius: 1.5, mt: 1, bgcolor: BACKGROUND_COLOR.WHITE }}
            />
          )}
          {googleMapUrl && (
            <GoFurtherExternalLink href={googleMapUrl} sx={{ display: "inline-flex", alignItems: "center", mt: 1, fontSize: FONT_SIZE.caption }}>
              <OpenInNewIcon sx={{ fontSize: "1rem", verticalAlign: "middle", mr: 0.5 }} />
              地図で確認する
            </GoFurtherExternalLink>
          )}
        </Box>
      </Grid>
      <Grid item xs={5} sx={{ display: "flex", width: "100%" }}>
        {goChargeUrl ? (
          <Link href={goChargeUrl} target="_blank" rel="noopener noreferrer" underline="none" sx={{ width: "100%" }}>
            <Box
              sx={{
                border: `1px solid ${BACKGROUND_COLOR.BORDER}`,
                backgroundImage: "url('/gocharge_map.png')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                borderRadius: 4,
                position: "relative",
                overflow: "hidden",
                height: "100%",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: TEXT_COLOR.CONTRAST,
                textAlign: "center",
                transition: "filter 0.2s ease",
                "&:hover": { filter: "brightness(1.2)" },
              }}
            >
              <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.24)" }} />
              <Box sx={{ position: "relative", zIndex: 1, px: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                <OpenInNewIcon sx={{ fontSize: FONT_SIZE.headline6, flexShrink: 0 }} />
                <Typography sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline6, textAlign: "left" }}>
                  GO Chargeで
                  <br />
                  充電器を探す
                </Typography>
              </Box>
            </Box>
          </Link>
        ) : (
          <Box
            sx={{
              borderRadius: 2,
              bgcolor: BACKGROUND_COLOR.WHITE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: TEXT_COLOR.SECONDARY,
              fontSize: FONT_SIZE.body2,
              width: "100%",
              minHeight: 320,
            }}
          >
            地図情報がありません
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

// GO Charge 拡張プラン申し込みパネル（CD ステップ1・Persona B セクション3 共通）
const GoChargeApplicationPanel: React.FC<{
  selectedOffice: OfficeWithPersona | null;
}> = ({ selectedOffice }) => (
  <Stack gap={2}>
    <Box sx={{ bgcolor: BACKGROUND_COLOR.WHITE, borderRadius: 2, p: 3, textAlign: "center" }}>
      <Typography sx={{ fontSize: FONT_SIZE.subtitle1, fontWeight: FONT_WEIGHT.BOLD, color: TEXT_COLOR.PRIMARY }}>
        GO Chargeカード拡張プランへのお申し込み
      </Typography>
      <Typography sx={{ mt: 1, fontSize: FONT_SIZE.body2, color: TEXT_COLOR.SECONDARY }}>
        月額: 税込
        <Box component="span" px={0.5} sx={{ color: TEXT_COLOR.SUCCESS, fontWeight: FONT_WEIGHT.BOLD }}>
          4,180
        </Box>
        円/台。1台からOK。解約も可能。
      </Typography>
      <Button
        variant="contained"
        sx={{
          mt: 2,
          fontSize: FONT_SIZE.body1,
          fontWeight: FONT_WEIGHT.BOLD,
          borderRadius: 99,
          px: 4,
          py: 1,
          bgcolor: BACKGROUND_COLOR.PRIMARY,
          textTransform: "none",
        }}
        onClick={() => {
          GoFurtherAnalytics.trackGoChargePlanClick(selectedOffice?.id, selectedOffice?.displayName);
          window.open(GetGoChargeApplicationFormUrl(), "_blank", "noopener,noreferrer");
        }}
        startIcon={<OpenInNewIcon sx={{ fontSize: "1rem" }} />}
      >
        GO Charge拡張プランを申し込む
      </Button>
    </Box>
    <Box>
      <Typography sx={{ fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY }}>
        ※上記のお申し込みは「ライトプラン×拡張プラン(eMPネットワーク)」を対象としております。
        <br />
        「スタンダードプラン」をご希望の場合は、担当より別途申込フォームをご案内させていただきます。
        <br />
        プランの詳細は
        <GoFurtherExternalLink href="https://go-charge.jp/pricing-business">
          料金プラン
        </GoFurtherExternalLink>
        をご確認ください
      </Typography>
    </Box>
  </Stack>
);

// ── Persona B レイアウト ─────────────────────────────────────────────────────

interface PersonaBLayoutProps {
  recommendedCharger: RecommendedCharger | null | undefined;
  streetViewEmbedUrl: string;
  googleMapUrl: string;
  goChargeUrl: string;
  selectedOffice: OfficeWithPersona | null;
  voiceItems: VoiceItem[];
  onDownload: () => void;
  isDownloading: boolean;
  hasDriverGuide: boolean;
}

const DriverGuideDownloadButton: React.FC<{
  onClick: () => void;
  disabled: boolean;
}> = ({ onClick, disabled }) => (
  <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
    <Button
      variant="contained"
      disabled={disabled}
      onClick={onClick}
      startIcon={<FileDownloadIcon />}
      sx={{
        borderRadius: 99,
        px: 4,
        py: 1,
        fontSize: FONT_SIZE.body1,
        fontWeight: FONT_WEIGHT.BOLD,
        textTransform: "none",
        bgcolor: BACKGROUND_COLOR.PRIMARY,
        color: TEXT_COLOR.CONTRAST,
        boxShadow: "none",
        "&:hover": { bgcolor: "#18a036", boxShadow: "none" },
      }}
    >
      乗務員様向けの配布資料をダウンロード
    </Button>
  </Box>
);

// FAQ: 充電不安を乗り越えて営業するには？（Persona B セクション2・CD 新カード 共通。外枠のスタイルは呼び出し側が用意する）
const ChargingAnxietyFaqBlock: React.FC = () => (
  <>
    <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
      <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
        <Typography sx={{ fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY }}>
          充電不安を乗り越えて営業するには？
        </Typography>
        <Typography sx={{ fontSize: FONT_SIZE.headline5, fontWeight: FONT_WEIGHT.BOLD }}>
          <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS }}>外部充電器を活用</Box>
          <Box component="span" sx={{ color: TEXT_COLOR.PRIMARY }}>すれば、翌日も営業可能</Box>
        </Typography>
      </Box>
      <Typography sx={{ fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY }}>
        「翌日の営業開始までに、十分な充電がなくて回せない」という声をいただくことがあります。
        <br />
        1日の中で車両を充電するタイミングを決めることで、LPGと同様に2人車運用を実現した事例がございます（図参考）。
      </Typography>
      <Typography sx={{ fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY }}>
        ※図は、隔日勤務の例で作成しております。日勤も対応可能で、シミュレーションを基にサポートさせていただきます。サポートを希望される場合、
        <Link href="mailto:gx_contact@goinc.jp" underline="none" sx={{ color: "inherit" }}>
          gx_contact@goinc.jp
        </Link>
        にご連絡ください
      </Typography>
    </Box>
    <Box
      component="img"
      src="/charging_model_case.png"
      alt="運用のモデルケース"
      sx={{ width: "100%", borderRadius: 2 }}
    />
  </>
);

// FAQ: 乗務員様のシフトや割当を変更するときの工夫は？（Persona B セクション2・CD 新カード 共通。外枠のスタイルは呼び出し側が用意する）
const ShiftAssignmentFaqBlock: React.FC<{
  onDownload: () => void;
  isDownloading: boolean;
  hasDriverGuide: boolean;
}> = ({ onDownload, isDownloading, hasDriverGuide }) => (
  <>
    <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
      <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
        <Typography sx={{ fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY }}>
          乗務員様のシフトや割当を変更するときの工夫は？
        </Typography>
        <Typography sx={{ fontSize: FONT_SIZE.headline5, fontWeight: FONT_WEIGHT.BOLD, color: TEXT_COLOR.PRIMARY }}>
          <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS }}>EVならではの魅力・特徴</Box>を乗務員様にお伝えする
        </Typography>
      </Box>
      <Typography sx={{ fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY }}>
        EVの運転や外部充電器の利用に、不安な乗務員様もいらっしゃるかと思います。
        <br />
        一方で実際に何度か乗務いただくことでEVへの評価が変わった事例もございます。
        <br />
        電気自動車の魅力としていただいたコメントを一部ご紹介します。
      </Typography>
      <Typography sx={{ fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY }}>
        まとめた資料は下記の「乗務員様向けの配布資料をダウンロード」ボタンよりダウンロードいただきご活用ください。
      </Typography>
    </Box>
    <Grid container spacing={2.5}>
      {EV_BENEFITS.map((benefit, i) => (
        <Grid item xs={4} key={benefit.title}>
          <Box
            sx={{
              bgcolor: BACKGROUND_COLOR.SECONDARY,
              borderRadius: 2,
              p: 2.5,
              textAlign: "center",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                bgcolor: BACKGROUND_COLOR.PRIMARY,
                color: TEXT_COLOR.CONTRAST,
                borderRadius: "50%",
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: FONT_SIZE.subtitle2,
                fontWeight: FONT_WEIGHT.BOLD,
                flexShrink: 0,
              }}
            >
              {i + 1}
            </Box>
            <Typography sx={{ fontSize: FONT_SIZE.headline5, fontWeight: FONT_WEIGHT.BOLD, color: TEXT_COLOR.SUCCESS }}>
              {benefit.title}
            </Typography>
            <Typography sx={{ fontSize: FONT_SIZE.body2, color: TEXT_COLOR.SECONDARY }}>
              {benefit.text}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
    <DriverGuideDownloadButton onClick={onDownload} disabled={!hasDriverGuide || isDownloading} />
  </>
);

const GoFurtherActionSectionPersonaB: React.FC<PersonaBLayoutProps> = ({
  recommendedCharger,
  streetViewEmbedUrl,
  googleMapUrl,
  goChargeUrl,
  selectedOffice,
  voiceItems,
  onDownload,
  isDownloading,
  hasDriverGuide,
}) => (
  <SectionGridItemCard
    title={
      <Typography component="h2" sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5 }}>
        明日からできる
        <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.headline4, ml: 1 }}>
          簡単アクション
        </Box>
        （事例紹介）
      </Typography>
    }
  >
    {/* イントロ */}
    <Grid item xs={12}>
      <Typography sx={{ fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY }}>
        「外部充電器の利用を促したい」「1台あたりのEV稼働を伸ばしたい」といったご要望に合わせて、LPG同等を達成している事業者様の成功事例をご紹介します。
        <br />
        貴社の運用における参考資料としてご活用ください。
      </Typography>
    </Grid>

    {/* セクション1: 外部充電器の利用を促したい */}
    <Grid item xs={12}>
      <SecondaryPanel sx={PERSONA_B_PANEL_SX}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box>
            <Typography component="span" sx={SECTION_CHIP_SX}>
              外部充電器の利用を促したい
            </Typography>
          </Box>
          <Typography sx={{ color: TEXT_COLOR.SUCCESS, fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5 }}>
            「必要なポイントの再周知」による外部充電利用の伸長
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
          <Typography sx={{ fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY }}>
            「どこで充電すればいいのか」「外部充電を使うメリット」などポイントを絞って乗務員様に再度お伝えいただくことで、外部充電の利用が伸びた成功事例がございます。
          </Typography>
          <Typography sx={{ fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY }}>
            下記の「おすすめの充電器」や「乗務員様の声」をぜひご参考になさってください。
            <br />
            まとめた資料は下記の「乗務員様向けの配布資料をダウンロード」ボタンよりダウンロードいただきご活用ください。
          </Typography>
        </Box>

        <RecommendedChargerPanel
          recommendedCharger={recommendedCharger}
          streetViewEmbedUrl={streetViewEmbedUrl}
          googleMapUrl={googleMapUrl}
          goChargeUrl={goChargeUrl}
        />

        {/* 乗務員の声：GoFurtherMotivationSection をそのまま内包 */}
        {voiceItems.length > 0 && (
          <GoFurtherMotivationSection voiceItems={voiceItems} />
        )}

        <DriverGuideDownloadButton onClick={onDownload} disabled={!hasDriverGuide || isDownloading} />
      </SecondaryPanel>
    </Grid>

    {/* セクション2: 1台あたりのEV稼働を伸ばしたい */}
    <Grid item xs={12}>
      <SecondaryPanel sx={PERSONA_B_PANEL_SX}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box>
            <Typography component="span" sx={SECTION_CHIP_SX}>
              1台あたりのEV稼働を伸ばしたい
            </Typography>
          </Box>
          <Typography sx={{ color: TEXT_COLOR.SUCCESS, fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5 }}>
            「乗務員様のシフト・割当の変更」による稼働の伸長
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
          <Typography sx={{ fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY }}>
            EVでも隔勤運用や2人車運用にすることで、1台あたりの稼働が伸びた成功事例がございます。
            <br />
            実際の成功事例を下記でご紹介します。
          </Typography>
        </Box>

        {/* FAQ1: 充電不安 */}
        <Box sx={PERSONA_B_WHITE_BLOCK_SX}>
          <ChargingAnxietyFaqBlock />
        </Box>

        {/* FAQ2: EV の魅力 */}
        <Box sx={PERSONA_B_WHITE_BLOCK_SX}>
          <ShiftAssignmentFaqBlock onDownload={onDownload} isDownloading={isDownloading} hasDriverGuide={hasDriverGuide} />
        </Box>
      </SecondaryPanel>
    </Grid>

    {/* セクション3: 一部車両でLPG同等走行を達成された場合、全車両へ展開をお願いします */}
    <Grid item xs={12}>
      <Typography sx={{ fontSize: FONT_SIZE.headline6, fontWeight: FONT_WEIGHT.BOLD, color: TEXT_COLOR.PRIMARY, textAlign: "center" }}>
        一部車両でLPG同等走行を達成された場合、全車両へ展開をお願いします
      </Typography>
    </Grid>

    {/* セクション4: 一部車両から全車両への展開 */}
    <Grid item xs={12}>
      <SecondaryPanel sx={PERSONA_B_PANEL_SX}>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
          <Typography sx={{ color: TEXT_COLOR.SUCCESS, fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5 }}>
            一部車両から全車両への展開
          </Typography>
          <Typography sx={{ fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY }}>
            一部車両でLPG同等走行を達成できたら、段階的に全車両で同様の取り組みを行うことで、更なる稼働・営収向上を見込めます。
          </Typography>
          <Typography sx={{ fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY }}>
            上記に伴い、全台でGO Chargeカードの拡張プランへのお申し込みが済んでいない場合、追加のお申し込み*をご検討いただければ幸いです。
          </Typography>
        </Box>
        <GoChargeApplicationPanel selectedOffice={selectedOffice} />
      </SecondaryPanel>
    </Grid>
  </SectionGridItemCard>
);

// ── CD レイアウト ─────────────────────────────────────────────────────────────

interface CDLayoutProps {
  recommendedCharger: RecommendedCharger | null | undefined;
  streetViewEmbedUrl: string;
  googleMapUrl: string;
  goChargeUrl: string;
  selectedOffice: OfficeWithPersona | null;
  voiceItems: VoiceItem[];
}

const GoFurtherActionSectionCD: React.FC<CDLayoutProps> = ({
  recommendedCharger,
  streetViewEmbedUrl,
  googleMapUrl,
  goChargeUrl,
  selectedOffice,
  voiceItems,
}) => (
  <SectionGridItemCard
    title={
      <Typography component="h2" sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5 }}>
        明日からの
        <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.headline4 }}>
          簡単アクション・3ステップ
        </Box>
      </Typography>
    }
  >
    <Grid item xs={12}>
      <SecondaryPanel sx={{ position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", gap: 3 }}>
        <Stack>
          <Typography sx={{ color: TEXT_COLOR.SUCCESS, fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5 }}>
            ステップ1
          </Typography>
          <Box sx={{ mt: 1, fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY, position: "relative", zIndex: 1 }}>
            <Typography component="p">
              対象車両を決めたら、まずは外部充電器が利用可能となる
              <br />
              GO Chargeカードの拡張プランへのお申し込み*をお願いいたします。
            </Typography>
            <Typography component="p" mt={0.5}>
              拡張プラン(eMPネットワーク)をお申し込みいただくと、
              <br />
              全国約10,000口の充電器を利用できるようになります。
            </Typography>
          </Box>
        </Stack>
        <Stack
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            minHeight: 170,
          }}
        >
          <Box
            sx={[
              SPEECH_BUBBLE_BASE_SX,
              {
                transform: "translateY(-32px)",
                "&::after": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  right: -12,
                  top: "25%",
                  width: 12,
                  height: 8,
                  bgcolor: BACKGROUND_COLOR.WHITE,
                  clipPath: "polygon(0 0, 100% 100%, 0 100%)",
                  filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.08))",
                },
              },
            ]}
          >
            <Typography sx={{ fontSize: FONT_SIZE.body2, fontWeight: FONT_WEIGHT.BOLD, color: TEXT_COLOR.PRIMARY }}>
              全国約
              <Box component="span" px={0.5} sx={{ fontSize: FONT_SIZE.body1, fontWeight: FONT_WEIGHT.BOLD, color: TEXT_COLOR.SUCCESS }}>
                10,000
              </Box>
              口を利用可能！
            </Typography>
            <Typography sx={SPEECH_BUBBLE_SUBTEXT_SX}>貴社の周辺にも設置あり！</Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minWidth: 150, px: 0.5 }}>
            <Box
              component="img"
              src="/gocharge_card.png"
              alt="GO Charge card"
              sx={{
                width: 180,
                maxWidth: "100%",
                filter: "drop-shadow(0 8px 12px rgba(0,0,0,0.16))",
                pointerEvents: "none",
                alignSelf: "center",
                ml: 0,
              }}
            />
          </Box>

          <Box
            sx={[
              SPEECH_BUBBLE_BASE_SX,
              {
                transform: "translateY(28px)",
                "&::before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  left: -12,
                  bottom: "25%",
                  width: 12,
                  height: 8,
                  bgcolor: BACKGROUND_COLOR.WHITE,
                  clipPath: "polygon(0 0, 100% 100%, 100% 0)",
                  filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.08))",
                },
                "&::after": { display: "none" },
              },
            ]}
          >
            <Typography sx={SPEECH_BUBBLE_SUBTEXT_SX}>拡張プラン加入の営業所では</Typography>
            <Typography sx={{ fontSize: FONT_SIZE.body2, color: TEXT_COLOR.PRIMARY }}>
              月間走行距離が
              <Box component="span" px={0.5} sx={{ fontSize: FONT_SIZE.body1, fontWeight: FONT_WEIGHT.BOLD, color: TEXT_COLOR.SUCCESS }}>
                平均1.37倍
              </Box>
              にUP！
            </Typography>
          </Box>
        </Stack>
        <GoChargeApplicationPanel selectedOffice={selectedOffice} />
      </SecondaryPanel>
    </Grid>

    <Grid item xs={12}>
      <SecondaryPanel>
        <Typography sx={{ color: TEXT_COLOR.SUCCESS, fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5 }}>
          ステップ2
        </Typography>
        <Typography sx={{ mt: 1, fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY }}>
          営業所や営業エリア周辺の外部充電器からおすすめの充電器を見つけられます。
          GO Chargeの「地図を検索」機能等を活用して、営業所や営業エリア周辺の充電器を探すことができます。
        </Typography>
        <Box sx={{ mt: 2 }}>
          <RecommendedChargerPanel
            recommendedCharger={recommendedCharger}
            streetViewEmbedUrl={streetViewEmbedUrl}
            googleMapUrl={googleMapUrl}
            goChargeUrl={goChargeUrl}
          />
        </Box>
      </SecondaryPanel>
    </Grid>

    <Grid item xs={12}>
      <SecondaryPanel>
        <Typography sx={{ color: TEXT_COLOR.SUCCESS, fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5 }}>
          ステップ3
        </Typography>
        <Typography sx={{ mt: 1, fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY }}>
          GO Chargeカードお申し込みから約1週間でカードが届きます。
          <br />
          対象車両の乗務員の方にカードをお渡しいただき、運行でのご利用をお願いいたします。
          <br />
          おすすめの充電器とあわせてお伝えいただくと、乗務員の方もより安心してEVを運行できます。
        </Typography>
      </SecondaryPanel>
    </Grid>

    <Grid item xs={12}>
      <Typography sx={{ fontSize: FONT_SIZE.caption, color: TEXT_COLOR.SECONDARY }}>
        * プロジェクト開始当初は外部充電は緊急時のみとなっておりましたが、弊社のGO Chargeカードをご利用頂ければ外部充電でも問題ないことをNEDOと合意いたしました。外部充電器をご利用頂くことで、導入頂いた20営業所のEV平均走行距離が37%増加しております。(弊社のGO Chargeカード以外でのご利用は引き続き緊急時のみ)
      </Typography>
    </Grid>

    {voiceItems.length > 0 && (
      <Grid item xs={12}>
        <SecondaryPanel sx={{ p: 2 }}>
          <Grid item xs={12}>
            <GoFurtherMotivationSection voiceItems={voiceItems} />
          </Grid>
        </SecondaryPanel>
      </Grid>
    )}
  </SectionGridItemCard>
);

// 「乗務員様のシフト・割当の変更」による稼働の伸長（CD 専用の新規カード。他社事例紹介として独立させる）
const GoFurtherEvOperationImprovementCard: React.FC<{
  onDownload: () => void;
  isDownloading: boolean;
  hasDriverGuide: boolean;
}> = ({ onDownload, isDownloading, hasDriverGuide }) => (
  <SectionGridItemBannerCard
    banner="他社様の成功事例紹介"
  >
    <Grid item xs={12}>
      <SecondaryPanel sx={PERSONA_B_PANEL_SX}>
        <Grid item xs={12}>
          <Typography component="h2" sx={{ color: TEXT_COLOR.SUCCESS, fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5 }}>
            「乗務員様のシフト・割当の変更」による稼働の伸長
          </Typography>
          <Typography sx={{ fontSize: FONT_SIZE.body1, color: TEXT_COLOR.PRIMARY }}>
            EVでも隔勤運用や2人車運用にすることで、1台あたりの稼働が伸びた成功事例がございます。
            <br />
            実際の成功事例を下記でご紹介します。
          </Typography>
        </Grid>

        {/* 乗務員様のシフトや割当を変更するときの工夫は？：AB向けと背景色・文言を変更せずそのまま表示 */}
        <Box sx={PERSONA_B_WHITE_BLOCK_SX}>
          <ShiftAssignmentFaqBlock onDownload={onDownload} isDownloading={isDownloading} hasDriverGuide={hasDriverGuide} />
        </Box>

        {/* 充電不安を乗り越えて営業するには？：AB向けと背景色・文言を変更せずそのまま表示 */}
        <Box sx={PERSONA_B_WHITE_BLOCK_SX}>
          <ChargingAnxietyFaqBlock />
        </Box>
      </SecondaryPanel>
    </Grid>
  </SectionGridItemBannerCard>
);

// ── メインコンポーネント ──────────────────────────────────────────────────────

const GoFurtherActionSection: React.FC<GoFurtherActionSectionProps> = ({
  recommendedCharger,
  streetViewEmbedUrl,
  googleMapUrl,
  goChargeUrl,
  selectedOffice,
  personaType,
  driverGuideId,
  voiceItems = [],
}) => {
  const isPersonaAB = personaType === "A" || (personaType?.startsWith("B") ?? false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDriverGuideDownload = useCallback(async () => {
    if (driverGuideId == null) return;
    setIsDownloading(true);
    try {
      GoFurtherAnalytics.trackDriverGuideDownload(selectedOffice?.id, selectedOffice?.displayName);
      const res = await getDriverGuideUrlAPI(driverGuideId);
      window.open(res.data.presignedUrl, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("資料のダウンロードに失敗しました。");
    } finally {
      setIsDownloading(false);
    }
  }, [driverGuideId, selectedOffice]);

  if (isPersonaAB) {
    return (
      <GoFurtherActionSectionPersonaB
        recommendedCharger={recommendedCharger}
        streetViewEmbedUrl={streetViewEmbedUrl}
        googleMapUrl={googleMapUrl}
        goChargeUrl={goChargeUrl}
        selectedOffice={selectedOffice}
        voiceItems={voiceItems}
        onDownload={handleDriverGuideDownload}
        isDownloading={isDownloading}
        hasDriverGuide={driverGuideId != null}
      />
    );
  }

  return (
    <>
      <GoFurtherActionSectionCD
        recommendedCharger={recommendedCharger}
        streetViewEmbedUrl={streetViewEmbedUrl}
        googleMapUrl={googleMapUrl}
        goChargeUrl={goChargeUrl}
        selectedOffice={selectedOffice}
        voiceItems={voiceItems}
      />
      <GoFurtherEvOperationImprovementCard
        onDownload={handleDriverGuideDownload}
        isDownloading={isDownloading}
        hasDriverGuide={driverGuideId != null}
      />
    </>
  );
};

export default GoFurtherActionSection;
